import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { Lock } from 'lucide-react';

interface PasswordChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PasswordChangeDialog = ({ open, onOpenChange }: PasswordChangeDialogProps) => {
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { changePassword } = useAdminAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwords.new !== passwords.confirm) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (passwords.new.length < 4) {
      toast({
        title: 'Error',
        description: 'Password must be at least 4 characters long',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    const result = await changePassword(passwords.current, passwords.new);
    
    if (result.success) {
      toast({
        title: 'Success',
        description: 'Password changed successfully'
      });
      setPasswords({ current: '', new: '', confirm: '' });
      onOpenChange(false);
    } else {
      toast({
        title: 'Error',
        description: result.error || 'Failed to change password',
        variant: 'destructive'
      });
    }
    
    setIsSubmitting(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setPasswords(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-glow-cyber">
            <Lock className="w-5 h-5" />
            Change Password
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="current">Current Password</Label>
            <Input
              id="current"
              type="password"
              value={passwords.current}
              onChange={(e) => handleInputChange('current', e.target.value)}
              placeholder="Enter current password"
              className="glass border-surface-variant focus:border-cyber-blue"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new">New Password</Label>
            <Input
              id="new"
              type="password"
              value={passwords.new}
              onChange={(e) => handleInputChange('new', e.target.value)}
              placeholder="Enter new password"
              className="glass border-surface-variant focus:border-cyber-blue"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm New Password</Label>
            <Input
              id="confirm"
              type="password"
              value={passwords.confirm}
              onChange={(e) => handleInputChange('confirm', e.target.value)}
              placeholder="Confirm new password"
              className="glass border-surface-variant focus:border-cyber-blue"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 glass"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-cyber text-primary-foreground hover-glow"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordChangeDialog;